import { useHistory } from 'react-router-dom';

type RouterHistory = ReturnType<typeof useHistory>;

/**
 * The router the module is mounted under, `undefined` when it is mounted without one — the
 * performance profile embedded in the host's colleague page, Storybook, a test that renders a page
 * on its own.
 *
 * Why this exists rather than a `try`/`catch` at each reader: `useHistory` is the one router hook of
 * react-router 5.3 that *answers* outside a Router instead of raising. It returns the history
 * context as it finds it, where `useLocation` and `useParams` read a property off that context and
 * throw a `TypeError` on `undefined`. Wrapping either of those in a `try` is calling a hook
 * conditionally, which changes the order of hooks between renders — the very thing
 * `react-hooks/rules-of-hooks` forbids, and which React has no way to recover from.
 *
 * Read what you need off the result: `?.location` for the current location and its state. A reader
 * that is always mounted under a Router does not come through here; it calls the hook it wants
 * directly.
 */
export const useOptionalRouter = (): RouterHistory | undefined =>
    // The declared return type never admits `undefined`, because the hook is written for callers
    // that are inside a Router. Outside one it hands back the empty context all the same.
    useHistory() as RouterHistory | undefined;
